import type { ChangeEventHandler, FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import IconMdi from '@components/IconMdi';
import Input from '@components/Input';
import type { InputFieldProps } from '@components/InputField';
import { InputField } from '@components/InputField';
import { Show, ShowFirstMatching } from '@components/Show';
import { Spinner } from '@components/Spinner';
import { TooltipedIcon } from '@components/TooltipedIcon';
import { VirtualizedList } from '@components/virtualization';
import type { Option } from '@hooks/use-fuse-search';
import { mdiCloseCircleOutline, mdiMagnify } from '@mdi/js';
import { isEmptyValue } from '@root/helpers';
import { useFuseSearch } from '@src/hooks';
import sharedClasses from '@utils/shared-classes';
import clsx from 'clsx';

import type { UIOnChangeFn, UISelectionType } from './types';

export interface SearchableInputSelectProps extends InputFieldProps {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  dataTestId?: string;
  id?: string;
  initialSelectedItem?: string;
  isLoading?: boolean;
  labelText?: string;
  name: string;
  noOptionsLeftLabel?: string;
  onChange: UIOnChangeFn;
  options: UISelectionType[];
  placeholder?: string;
  /** Custom option row content. Defaults to the plain truncated label when omitted. */
  renderOption?: (option: UISelectionType) => ReactNode;
  value?: string;
}

const INNER_CONTAINER_HEIGHT_PX = 398;

export const SearchableInputSelect: FC<SearchableInputSelectProps> = ({
  dataTestId,
  isDisabled,
  isLoading = false,
  name,
  noOptionsLeftLabel = 'No existing options available',
  onChange,
  options,
  placeholder,
  renderOption,
  value,
  ...props
}) => {
  const [searchText, setSearchText] = useState('');

  const { onSearch, searchResult } = useFuseSearch({
    options: options.map(({ name, value }) => ({ label: name, value: value as string })),
    searchText,
  });

  useEffect(() => {
    if (value) {
      const selected = options.find((item) => item.value === value)?.name;
      setSearchText(selected ?? '');
    }
  }, [name, onChange, onSearch, options, value]);

  useEffect(() => {
    if (!value) {
      setSearchText('');
      onSearch(undefined);
    }
  }, [onSearch, value]);

  const changeHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newValue = event.currentTarget.value;
    setSearchText(newValue);
    return onSearch(newValue);
  };

  return (
    <InputField {...props} isDisabled={isDisabled || isLoading}>
      <div
        className={clsx(
          'border-info-300 flex flex-col rounded-lg border shadow-xs',
          isDisabled && sharedClasses.disabled,
        )}
      >
        <div className="border-info-300 relative flex justify-center gap-2 border-b">
          <IconMdi path={mdiMagnify} size={0.75} className="text-info-400 absolute top-1/2 left-2 -translate-y-1/2" />
          <Input
            autoFocus
            className="border-none pl-8 focus:outline-hidden"
            disabled={isLoading}
            key={value}
            name={name}
            onChange={changeHandler}
            placeholder={placeholder}
            value={searchText}
            dataTestId={`${dataTestId}:search`}
          />
          <Show when={!isEmptyValue(value)}>
            <TooltipedIcon
              placement="top"
              path={mdiCloseCircleOutline}
              className="text-info-400 hover:text-error-500 mr-2 cursor-pointer transition-colors"
              size={0.625}
              content="Remove selection"
              onClick={() => onChange(undefined, name)}
              dataTestId={dataTestId ? `${dataTestId}:clear` : 'clear'}
            />
          </Show>
          <Show when={isLoading}>
            <Spinner className="[&>path]:fill-info-400 absolute top-3 right-3" size="xs" />
          </Show>
        </div>
        <ul className="relative flex h-44 flex-col gap-2 overflow-y-auto py-1 text-gray-700">
          <ShowFirstMatching>
            <Show when={isLoading}>
              <>
                {new Array(4).fill(null).map((_, idx) => (
                  <li className="flex animate-pulse px-3 py-2" key={`searchable-input-select-skeleton-${idx}`}>
                    <p className="h-4 w-3/4 rounded-lg bg-slate-200" />
                  </li>
                ))}
              </>
            </Show>
            <Show when={!isLoading}>
              <VirtualizedList<Option>
                data={searchResult}
                itemHeight={36}
                height={INNER_CONTAINER_HEIGHT_PX}
                renderItem={({ label, value: fieldValue }) => (
                  <li
                    className={clsx(
                      'hover:bg-info-100 flex h-full min-w-0 cursor-pointer items-center overflow-hidden px-3 py-2 text-sm leading-5 font-normal transition-colors',
                      fieldValue === value && 'bg-info-100',
                    )}
                    key={fieldValue}
                    onClick={() => onChange(fieldValue, name)}
                    data-testid={`${dataTestId}:option-${fieldValue}`}
                  >
                    {renderOption ? (
                      <div className="w-full min-w-0">{renderOption({ name: label, value: fieldValue })}</div>
                    ) : (
                      <span className="w-full truncate">{label}</span>
                    )}
                  </li>
                )}
              />
              <Show when={searchResult.length === 0}>
                <span className="absolute inset-0 p-3 text-sm leading-5 font-normal text-gray-400">
                  {noOptionsLeftLabel}
                </span>
              </Show>
            </Show>
          </ShowFirstMatching>
        </ul>
      </div>
    </InputField>
  );
};
